/**
 * Cross-runtime env-var resolver.
 *
 * In Vite-built code, values come from `import.meta.env` (injected at
 * build time). In Node contexts (Admin scripts, Vitest runner), values
 * come from `process.env`. This helper unifies both so callers can use
 * one API regardless of where the module loads.
 *
 * Critical: never log the resolved value itself, only the variable
 * name. The error message from `requireEnv` must NOT include the
 * actual value (even partially) to avoid leaking secrets-equivalent
 * values into logs or stack traces.
 */

/**
 * Read an environment variable, returning `undefined` if it's not set
 * or is an empty string. Checks `import.meta.env` first (Vite/browser),
 * then `process.env` (Node).
 *
 * @param {string} name - The variable name (e.g. `VITE_SUPABASE_URL`)
 * @returns {string | undefined}
 */
export function readEnv(name) {
  // 1. Vite/browser context: import.meta.env is populated at build time.
  try {
    if (typeof import.meta !== 'undefined' && import.meta && import.meta.env) {
      const v = import.meta.env[name];
      if (v !== undefined && v !== '') return v;
    }
  } catch (_) {
    // Not in a Vite/ESM context that supports import.meta.env — fall through.
  }

  // 2. Node context: process.env (Admin scripts, Vitest fallback).
  if (typeof process !== 'undefined' && process && process.env) {
    const v = process.env[name];
    if (v !== undefined && v !== '') return v;
  }

  return undefined;
}

/**
 * Read a required environment variable. Throws a descriptive error
 * naming the variable (but NEVER printing its value) if unset.
 *
 * Throwing at module load is intentional: it surfaces misconfiguration
 * immediately at startup rather than letting `undefined` propagate into
 * checkout/billing calls and fail silently at runtime.
 *
 * @param {string} name - The variable name to require.
 * @returns {string}
 */
export function requireEnv(name) {
  const v = readEnv(name);
  if (v === undefined) {
    throw new Error(
      `Missing required environment variable: ${name}. ` +
      `Set it in Visualizer/.env.local (see .env.example) or your deployment environment.`
    );
  }
  return v;
}

/**
 * Read an optional environment variable, returning the provided default
 * if unset.
 *
 * @param {string} name - The variable name to look up.
 * @param {string | undefined} defaultValue - Value to return when unset.
 * @returns {string | undefined}
 */
export function optionalEnv(name, defaultValue) {
  const v = readEnv(name);
  return v !== undefined ? v : defaultValue;
}
